/**
 * Text セクション。4 variants:
 *   simple     — 通常段落 (HTML 可)
 *   two_column — 2 段組
 *   quote_box  — 引用枠 (左ボーダー + イタリック + 引用元)
 *   callout    — 色付きインフォボックス (info/warning/success)
 */
import { FileText } from 'lucide-react';

import { SelectField, TextField } from './primitives';
import { RichTextField } from './RichTextField';
import type { EditorProps, RendererProps, SectionDefinition } from './types';

interface SimpleConfig {
  html: string;
}
interface TwoColumnConfig {
  /** 左カラム本文 (HTML)。旧データ互換のため html フィールドからも fallback */
  leftHtml: string;
  /** 右カラム本文 (HTML) */
  rightHtml: string;
  /** @deprecated 旧形式。leftHtml が未設定で html がある場合は左カラムに移行する */
  html?: string;
}
interface QuoteConfig {
  html: string;
  citation: string;
}
type CalloutTone = 'info' | 'warning' | 'success' | 'note';
interface CalloutConfig {
  html: string;
  tone: CalloutTone;
  title: string;
}

function SimpleEditor({ config, onChange, onCommit }: EditorProps) {
  return (
    <RichTextField
      label="本文"
      value={(config.html as string) ?? ''}
      onChange={(v) => onChange({ ...config, html: v })}
      onCommit={onCommit}
      placeholder="本文をここに記述します..."
      minHeight={260}
    />
  );
}

function SimpleText({ config }: RendererProps) {
  const html = ((config as unknown as SimpleConfig).html ?? '') as string;
  return (
    <section
      className="prose dark:prose-invert max-w-none"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function readTwoColumn(config: Record<string, unknown>): {
  leftHtml: string;
  rightHtml: string;
} {
  const left = (config.leftHtml as string) ?? '';
  const right = (config.rightHtml as string) ?? '';
  // 旧データ互換: html だけがあれば左カラムにマイグレート
  if (!left && !right && typeof config.html === 'string') {
    return { leftHtml: config.html, rightHtml: '' };
  }
  return { leftHtml: left, rightHtml: right };
}

function TwoColumnEditor({ config, onChange, onCommit }: EditorProps) {
  const v = readTwoColumn(config);
  const set = (patch: Partial<TwoColumnConfig>) => {
    // 旧 html フィールドを除去しつつ新フィールドへ
    const { html: _legacy, ...rest } = config as Record<string, unknown> & {
      html?: string;
    };
    onChange({
      ...rest,
      leftHtml: patch.leftHtml ?? v.leftHtml,
      rightHtml: patch.rightHtml ?? v.rightHtml,
    });
  };
  return (
    <div className="space-y-3">
      <RichTextField
        label="左カラム"
        value={v.leftHtml}
        onChange={(x) => set({ leftHtml: x })}
        onCommit={onCommit}
        placeholder="左カラムの本文..."
        minHeight={160}
      />
      <RichTextField
        label="右カラム"
        value={v.rightHtml}
        onChange={(x) => set({ rightHtml: x })}
        onCommit={onCommit}
        placeholder="右カラムの本文..."
        minHeight={160}
      />
    </div>
  );
}

function TwoColumnText({ config }: RendererProps) {
  const v = readTwoColumn(config);
  return (
    <section className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
      <div
        className="prose dark:prose-invert max-w-none"
        dangerouslySetInnerHTML={{ __html: v.leftHtml }}
      />
      <div
        className="prose dark:prose-invert max-w-none"
        dangerouslySetInnerHTML={{ __html: v.rightHtml }}
      />
    </section>
  );
}

function QuoteEditor({ config, onChange, onCommit }: EditorProps) {
  const set = (patch: Partial<QuoteConfig>) =>
    onChange({ ...config, ...patch });
  return (
    <div className="space-y-3">
      <RichTextField
        label="引用文"
        value={(config.html as string) ?? ''}
        onChange={(v) => set({ html: v })}
        onCommit={onCommit}
        placeholder="引用したい本文..."
        minHeight={140}
      />
      <TextField
        label="引用元"
        value={(config.citation as string) ?? ''}
        onChange={(v) => set({ citation: v })}
        onCommit={onCommit}
        placeholder="— 山田 太郎, 株式会社サンプル"
      />
    </div>
  );
}

function QuoteText({ config }: RendererProps) {
  const v = config as unknown as QuoteConfig;
  return (
    <section className="border-l-4 border-[var(--accent)] pl-6 py-2">
      <div
        className="prose dark:prose-invert max-w-none italic text-lg"
        dangerouslySetInnerHTML={{ __html: v.html ?? '' }}
      />
      {v.citation && (
        <div className="mt-3 text-sm text-[var(--text-muted)]">
          {v.citation}
        </div>
      )}
    </section>
  );
}

function CalloutEditor({ config, onChange, onCommit }: EditorProps) {
  const set = (patch: Partial<CalloutConfig>) =>
    onChange({ ...config, ...patch });
  return (
    <div className="space-y-3">
      <SelectField<CalloutTone>
        label="トーン"
        value={(config.tone as CalloutTone) ?? 'info'}
        onChange={(v) => set({ tone: v })}
        onCommit={onCommit}
        options={[
          { value: 'info', label: 'インフォ (青)' },
          { value: 'warning', label: '注意 (黄)' },
          { value: 'success', label: '成功 (緑)' },
          { value: 'note', label: 'ノート (グレー)' },
        ]}
      />
      <TextField
        label="見出し"
        value={(config.title as string) ?? ''}
        onChange={(v) => set({ title: v })}
        onCommit={onCommit}
        placeholder="Note"
      />
      <RichTextField
        label="本文"
        value={(config.html as string) ?? ''}
        onChange={(v) => set({ html: v })}
        onCommit={onCommit}
        placeholder="伝えたい内容..."
        minHeight={140}
      />
    </div>
  );
}

const TONE_STYLES: Record<CalloutTone, string> = {
  info: 'bg-blue-50 border-blue-300 text-blue-900 dark:bg-blue-900/20 dark:text-blue-200',
  warning:
    'bg-amber-50 border-amber-300 text-amber-900 dark:bg-amber-900/20 dark:text-amber-200',
  success:
    'bg-emerald-50 border-emerald-300 text-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-200',
  note: 'bg-gray-50 border-gray-300 text-gray-900 dark:bg-gray-800/40 dark:text-gray-200',
};

function CalloutText({ config }: RendererProps) {
  const v = config as unknown as CalloutConfig;
  const tone = (v.tone ?? 'info') as CalloutTone;
  return (
    <section
      className={`border-l-4 rounded p-4 ${TONE_STYLES[tone] ?? TONE_STYLES.info}`}
    >
      {v.title && <div className="font-semibold mb-1">{v.title}</div>}
      <div
        className="prose dark:prose-invert max-w-none prose-sm"
        dangerouslySetInnerHTML={{ __html: v.html ?? '' }}
      />
    </section>
  );
}

export const textDefinition: SectionDefinition = {
  type: 'text',
  label: 'テキスト',
  icon: FileText,
  description: '本文・引用・コールアウトなど、文章主体のセクション。',
  defaultVariant: 'simple',
  useCases: ['all'],
  variants: {
    simple: {
      label: 'シンプル',
      description: '通常の段落 (HTML 可)',
      Renderer: SimpleText,
      Editor: SimpleEditor,
      sample: () => ({
        html: '<p>本文をここに記述します。<strong>強調</strong>や <a href="#">リンク</a>も使えます。</p>',
      }),
    },
    two_column: {
      label: '2段組',
      description: '左右に独立した本文を並べて配置',
      Renderer: TwoColumnText,
      Editor: TwoColumnEditor,
      sample: () => ({
        leftHtml:
          '<p><strong>【できること】</strong></p><ul><li>項目 1</li><li>項目 2</li><li>項目 3</li></ul>',
        rightHtml:
          '<p><strong>【注意事項】</strong></p><ul><li>注意 1</li><li>注意 2</li></ul>',
      }),
    },
    quote_box: {
      label: '引用',
      description: '左ボーダー + イタリック + 引用元',
      Renderer: QuoteText,
      Editor: QuoteEditor,
      sample: () => ({
        html: '<p>導入してから業務効率が劇的に改善しました。</p>',
        citation: '— 田中 様, 製造業',
      }),
    },
    callout: {
      label: 'コールアウト',
      description: '色付きインフォボックス',
      Renderer: CalloutText,
      Editor: CalloutEditor,
      sample: () => ({
        tone: 'info' as CalloutTone,
        title: 'ポイント',
        html: '<p>重要なポイントをここに記述します。</p>',
      }),
    },
  },
};
